/**
 * StudentTerminal
 *
 * Modal that opens a live shell into a student's running pod for an
 * instructor to debug/help. Wires xterm.js to the WebSocket gateway via
 * the `instructor:terminal:*` event family.
 *
 * Lifecycle:
 *   mount   -> emit instructor:terminal:connect, subscribe to output/status
 *   typing  -> emit instructor:terminal:input
 *   resize  -> FitAddon re-fits, future: emit resize event
 *   unmount -> emit instructor:terminal:disconnect, unsubscribe, dispose xterm
 *
 * Closes on: backdrop click, Escape key, the Disconnect / X buttons.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Terminal as XTerm } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import 'xterm/css/xterm.css';
import { wsService } from '../services/websocket';
import styles from './StudentTerminal.module.css';

interface StudentTerminalProps {
  environmentId: string;
  studentName: string;
  studentEmail: string;
  onClose: () => void;
}

type ConnectionStatus = 'connecting' | 'connected' | 'disconnected';

const StudentTerminal: React.FC<StudentTerminalProps> = ({
  environmentId,
  studentName,
  studentEmail,
  onClose,
}) => {
  const terminalHostRef = useRef<HTMLDivElement>(null);
  const xtermRef = useRef<XTerm | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>('connecting');

  // Stable ref to the close handler so the Escape listener doesn't churn
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // ---------------------------------------------------------------------
  // xterm + websocket wiring
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!terminalHostRef.current) return;

    const term = new XTerm({
      cursorBlink: true,
      fontSize: 13,
      fontFamily: "'Fira Code', 'Monaco', Consolas, monospace",
      convertEol: true,
      theme: {
        background: '#0a0c10',
        foreground: '#e5e7eb',
        cursor: '#22c55e',
        selectionBackground: '#1f2937',
        black: '#000000',
        red: '#f87171',
        green: '#34d399',
        yellow: '#fbbf24',
        blue: '#60a5fa',
        magenta: '#c084fc',
        cyan: '#22d3ee',
        white: '#e5e7eb',
        brightBlack: '#4b5563',
        brightRed: '#fca5a5',
        brightGreen: '#6ee7b7',
        brightYellow: '#fcd34d',
        brightBlue: '#93c5fd',
        brightMagenta: '#d8b4fe',
        brightCyan: '#67e8f9',
        brightWhite: '#f9fafb',
      },
    });

    const fitAddon = new FitAddon();
    term.loadAddon(fitAddon);
    term.open(terminalHostRef.current);

    // Initial fit needs to happen after layout settles; rAF is enough.
    requestAnimationFrame(() => {
      try {
        fitAddon.fit();
      } catch {
        // Container may not be measurable yet (e.g. zero-height); ignore.
      }
    });

    xtermRef.current = term;
    fitAddonRef.current = fitAddon;

    // Pipe user keystrokes -> server
    const inputDisposable = term.onData((data: string) => {
      wsService.emit('instructor:terminal:input', { environmentId, data });
    });

    // Subscribe to output stream
    const unsubOutput = wsService.on<{ data: string }>(
      'instructor:terminal:output',
      ({ data }) => {
        xtermRef.current?.write(data);
      },
    );

    // Subscribe to status changes
    const unsubStatus = wsService.on<{ status: string; message?: string }>(
      'instructor:terminal:status',
      ({ status: next, message }) => {
        if (next === 'connected') {
          setStatus('connected');
        } else if (next === 'error') {
          setStatus('disconnected');
          xtermRef.current?.write(
            `\r\n\x1b[31mConnection error${message ? `: ${message}` : ''}\x1b[0m\r\n`,
          );
        } else if (next === 'disconnected') {
          setStatus('disconnected');
        }
      },
    );

    // Resize handling — window + container observer
    const handleWindowResize = () => {
      try {
        fitAddonRef.current?.fit();
      } catch {
        /* noop */
      }
    };
    window.addEventListener('resize', handleWindowResize);

    const resizeObserver = new ResizeObserver(() => {
      try {
        fitAddonRef.current?.fit();
      } catch {
        /* noop */
      }
    });
    resizeObserver.observe(terminalHostRef.current);

    // Kick off the exec session
    wsService.emit('instructor:terminal:connect', { environmentId });

    return () => {
      // Tell the server we're done — pod keeps running, just our exec ends.
      wsService.emit('instructor:terminal:disconnect', { environmentId });
      unsubOutput();
      unsubStatus();
      inputDisposable.dispose();
      window.removeEventListener('resize', handleWindowResize);
      resizeObserver.disconnect();
      term.dispose();
      xtermRef.current = null;
      fitAddonRef.current = null;
    };
  }, [environmentId]);

  // ---------------------------------------------------------------------
  // Escape key closes the modal
  // ---------------------------------------------------------------------
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  const statusLabel = useMemo<{ text: string; dot: string }>(() => {
    if (status === 'connecting') {
      return { text: 'Connecting…', dot: styles.statusConnecting };
    }
    if (status === 'connected') {
      return { text: 'Connected', dot: styles.statusConnected };
    }
    return { text: 'Disconnected', dot: styles.statusDisconnected };
  }, [status]);

  return (
    <div
      className={styles.overlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Terminal session for ${studentName}`}
    >
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.banner}>
          <div className={styles.bannerLeft}>
            <span
              className={`${styles.statusDot} ${statusLabel.dot}`}
              aria-label={statusLabel.text}
              title={statusLabel.text}
            />
            <div className={styles.bannerText}>
              <span className={styles.bannerTitle}>
                {statusLabel.text} · {studentName}&apos;s environment ({studentEmail})
              </span>
              <span className={styles.bannerWarning}>
                Be mindful — the student can see what you type.
              </span>
            </div>
          </div>
          <div className={styles.bannerActions}>
            <button
              type="button"
              className={styles.disconnectBtn}
              onClick={onClose}
            >
              Disconnect
            </button>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={onClose}
              aria-label="Close terminal"
              title="Close"
            >
              ✕
            </button>
          </div>
        </div>

        <div className={styles.terminalWrapper}>
          <div ref={terminalHostRef} className={styles.terminal} />
        </div>

        <div className={styles.footer}>
          <div className={styles.auditNote}>
            <span className={styles.auditIcon} aria-hidden="true" />
            <span>Connections and session activity are recorded</span>
          </div>
          <span className={styles.envIdLabel}>env: {environmentId}</span>
        </div>
      </div>
    </div>
  );
};

export default StudentTerminal;
